import { Outlet, NavLink, useLocation } from 'react-router-dom';
import type { ReactNode } from 'react';
import { useAuth } from '../hooks/useAuth';

interface NavItem {
  name: string;
  path: string;
  icon: ReactNode;
}

const stroke = {
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.6,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
};

const navigation: NavItem[] = [
  {
    name: 'Overview',
    path: '/',
    icon: (
      <svg viewBox="0 0 24 24" className="h-[18px] w-[18px]" {...stroke}>
        <path d="M3 13h8V3H3zM13 21h8v-10h-8zM13 3v4h8V3zM3 17v4h8v-4z" />
      </svg>
    ),
  },
  {
    name: 'Users',
    path: '/users',
    icon: (
      <svg viewBox="0 0 24 24" className="h-[18px] w-[18px]" {...stroke}>
        <circle cx="9" cy="8" r="3.2" />
        <path d="M3.5 20a5.5 5.5 0 0 1 11 0M16 5.5a3 3 0 0 1 0 5.6M17 14.5a5.5 5.5 0 0 1 3.5 5.1" />
      </svg>
    ),
  },
  {
    name: 'Recipes',
    path: '/recipes',
    icon: (
      <svg viewBox="0 0 24 24" className="h-[18px] w-[18px]" {...stroke}>
        <path d="M6 3v18M6 8h3M9 3v6a3 3 0 0 1-3 3M18 3c-1.5 0-2.5 2-2.5 5s1 4 2.5 4v9" />
      </svg>
    ),
  },
  {
    name: 'Worker',
    path: '/worker',
    icon: (
      <svg viewBox="0 0 24 24" className="h-[18px] w-[18px]" {...stroke}>
        <circle cx="12" cy="12" r="3" />
        <path d="M12 3v3M12 18v3M3 12h3M18 12h3M5.6 5.6l2.1 2.1M16.3 16.3l2.1 2.1M18.4 5.6l-2.1 2.1M7.7 16.3l-2.1 2.1" />
      </svg>
    ),
  },
  {
    name: 'Proxies',
    path: '/proxies',
    icon: (
      <svg viewBox="0 0 24 24" className="h-[18px] w-[18px]" {...stroke}>
        <circle cx="5" cy="12" r="2.2" />
        <circle cx="19" cy="6" r="2.2" />
        <circle cx="19" cy="18" r="2.2" />
        <path d="M7.2 11.2 16.9 6.9M7.2 12.8l9.7 4.3" />
      </svg>
    ),
  },
  {
    name: 'Links',
    path: '/links',
    icon: (
      <svg viewBox="0 0 24 24" className="h-[18px] w-[18px]" {...stroke}>
        <path d="M10 13a3.5 3.5 0 0 0 5 0l3-3a3.5 3.5 0 1 0-5-5l-1.5 1.5" />
        <path d="M14 11a3.5 3.5 0 0 0-5 0l-3 3a3.5 3.5 0 1 0 5 5L12.5 17.5" />
      </svg>
    ),
  },
];

export const AdminLayout: React.FC = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const current =
    [...navigation].reverse().find((n) => n.path !== '/' && location.pathname.startsWith(n.path)) ??
    navigation[0];

  return (
    <div className="min-h-screen text-cream-200">
      <div className="flex">
        {/* Sidebar */}
        <aside className="sticky top-0 flex h-screen w-64 shrink-0 flex-col border-r border-cream-600/15 bg-ink-900/60 backdrop-blur-md">
          <div className="flex items-center gap-3 px-6 py-6">
            <span className="grid h-9 w-9 place-items-center rounded-xl bg-saffron-500/15 text-saffron-300 ring-1 ring-saffron-500/30">
              <svg viewBox="0 0 24 24" className="h-5 w-5" {...stroke}>
                <path d="M7 11a5 5 0 0 1 10 0M5 11h14l-1.2 8.2a1 1 0 0 1-1 .8H7.2a1 1 0 0 1-1-.8z" />
              </svg>
            </span>
            <div className="leading-tight">
              <div className="font-display text-lg font-bold text-cream-50">Chefty</div>
              <div className="text-[0.66rem] uppercase tracking-[0.22em] text-cream-400">
                Console
              </div>
            </div>
          </div>

          <nav className="mt-2 flex flex-col gap-1 px-3">
            {navigation.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                end={item.path === '/'}
                className={({ isActive }) =>
                  `group relative flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors ${
                    isActive
                      ? 'bg-saffron-500/10 text-cream-50'
                      : 'text-cream-400 hover:bg-cream-600/5 hover:text-cream-200'
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    {isActive && (
                      <span className="absolute left-0 top-1/2 h-5 w-[3px] -translate-y-1/2 rounded-r-full bg-saffron-400" />
                    )}
                    <span className={isActive ? 'text-saffron-300' : ''}>{item.icon}</span>
                    {item.name}
                  </>
                )}
              </NavLink>
            ))}
          </nav>

          <div className="mt-auto border-t border-cream-600/15 p-4">
            <div className="flex items-center gap-3 rounded-xl bg-ink-850/60 p-3">
              <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-teal-500/15 font-mono text-sm text-teal-400 ring-1 ring-teal-500/25">
                {(user?.email?.[0] ?? 'A').toUpperCase()}
              </span>
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm text-cream-100" title={user?.email ?? undefined}>
                  {user?.email ?? 'Admin'}
                </div>
                <div className="text-[0.66rem] uppercase tracking-wider text-cream-400">
                  Administrator
                </div>
              </div>
            </div>
            <button
              onClick={logout}
              className="mt-2 w-full rounded-lg border border-cream-600/15 px-3 py-2 text-xs font-medium text-cream-400 transition-colors hover:border-bad/40 hover:text-bad"
            >
              Sign out
            </button>
          </div>
        </aside>

        {/* Main */}
        <div className="flex min-w-0 flex-1 flex-col">
          <header className="sticky top-0 z-10 border-b border-cream-600/15 bg-ink-950/70 px-8 py-5 backdrop-blur-md">
            <div className="flex items-center gap-3">
              <span className="text-cream-300">{current.icon}</span>
              <h1 className="font-display text-xl font-bold text-cream-50">{current.name}</h1>
            </div>
          </header>
          <main className="flex-1 px-8 py-7">
            <Outlet />
          </main>
        </div>
      </div>
    </div>
  );
};
